import { createAdminApiUrl } from "./api-url";

type ApiRequestOptions = {
  method?: "GET" | "POST" | "PATCH";
  body?: unknown;
  headers?: Record<string, string>;
};

/**
 * 管理APIの失敗
 */
export class AdminApiError extends Error {
  /** HTTPステータス */
  readonly status: number;

  /**
   * 管理APIの失敗を生成する
   *
   * @param status - HTTPステータス
   */
  constructor(status: number) {
    super("Admin API request failed");
    this.name = "AdminApiError";
    this.status = status;
  }
}

/**
 * オブジェクトとして扱える値か判定する
 *
 * @param value - 判定対象
 * @returns オブジェクトの場合はtrue
 */
const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === "object" && value !== null;
};

/**
 * Cookie認証付きで管理APIを呼び出す
 *
 * @param path - APIパス
 * @param options - リクエスト設定
 * @returns レスポンスのdata
 * @throws 管理APIが成功しなかった場合
 */
export const requestAdminApi = async <ResponseData>(
  path: string,
  options: ApiRequestOptions = {},
): Promise<ResponseData> => {
  try {
    const response = await fetch(createAdminApiUrl(path), {
      method: options.method ?? "GET",
      credentials: "include",
      headers: {
        ...(options.body === undefined ? {} : { "Content-Type": "application/json" }),
        ...options.headers,
      },
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
    });

    const payload: unknown = await response.json();

    if (!response.ok || !isRecord(payload) || payload.ok !== true || !("data" in payload)) {
      throw new AdminApiError(response.status);
    }

    return payload.data as ResponseData;
  } catch (error: unknown) {
    if (error instanceof AdminApiError) {
      throw error;
    }

    console.error("Admin API request failed", error);
    throw new AdminApiError(0);
  }
};

/**
 * クエリ文字列付きのAPIパスを生成する
 *
 * @param path - APIパス
 * @param parameters - クエリ項目
 * @returns クエリ文字列付きパス
 */
export const createApiPath = (
  path: string,
  parameters: Record<string, string | number | undefined>,
) => {
  const searchParameters = new URLSearchParams();

  for (const [key, value] of Object.entries(parameters)) {
    if (value !== undefined && value !== "") {
      searchParameters.set(key, String(value));
    }
  }

  const query = searchParameters.toString();

  return query ? `${path}?${query}` : path;
};
